// =============================================================================
// src/views/impostometro/impostometro-markup.ts — Marcacao fiel do Impostometro.
//
// Proveniencia:
//  - renderImpostometroSection: unified.html linhas 1064-1152 (section completa,
//    incluindo a mode-bar de tributos/anos, o resumo, o ranking ESTATICO inicial
//    e os CTAs com onclick="sendPrompt(...)"). Copiado caractere a caractere para
//    paridade de marcacao (mesmas tags/classes/ids/atributos/textos).
//  - buildCounterOverlayHtml: assets/unified-app.js linhas 1914-1924
//    (Sections.impostometro._buildCounterOverlay). Monta os headers do split, o
//    divisor e o contador flutuante injetados em #mapOverlays.
//
// Regra de fidelidade: NAO alterar markup. O ranking estatico aqui e SUBSTITUIDO
// em runtime por _updateRanking (impostometro-view.ts) ao ativar a secao; ambos
// sao portados verbatim para preservar o estado inicial e o pos-ativacao.
// =============================================================================

import type { FiscalYearEntry, TaxKind, FiscalYear } from '../../app/types';
import { buildProvenanceFooterMarkup } from '../../data/data-provenance';
import { yoyDeltaHtml } from './impostometro-view';

/**
 * HTML literal da `<section>` do Impostometro (paridade com unified.html).
 * Estado inicial: tributo ICMS, ano 2025, ranking estatico de 6 zonas.
 * @returns string HTML da secao, pronta para concatenar em #contentCol.
 */
export function renderImpostometroSection(): string {
  return (
    '<section class="u-section" id="section-impostometro" data-section="impostometro">' +
    '<div class="u-section-header">' +
    '<div class="u-section-title">Impostômetro Eleitoral</div>' +
    '<div class="u-section-subtitle">Arrecadação fiscal por zona vs. retorno em serviços públicos</div>' +
    '</div>' +
    '<div class="u-section-body">' +
    renderModeBar() +
    '<div class="u-panel">' +
    renderSummaryGrid() +
    renderStaticRanking() +
    renderCtaRow() +
    '</div>' +
    '</div>' +
    buildProvenanceFooterMarkup('impostometro') +
    '</section>'
  );
}

/** Barra de modos: tabs de tributo (ICMS/ISS) + tabs de ano (2020-2025). */
function renderModeBar(): string {
  return (
    '<div class="u-mode-bar">' +
    '<div class="u-mode-tabs">' +
    '<button class="u-tributo-tab active" data-tributo="icms"><i class="ti ti-building-bank" style="font-size:12px"></i> Estadual ICMS</button>' +
    '<button class="u-tributo-tab" data-tributo="iss"><i class="ti ti-building-community" style="font-size:12px"></i> Municipal ISS</button>' +
    '</div>' +
    '<div style="display:flex;gap:4px;margin-left:auto">' +
    renderYearTab('2020', false) +
    renderYearTab('2021', false) +
    renderYearTab('2022', false) +
    renderYearTab('2023', false) +
    renderYearTab('2024', false) +
    renderYearTab('2025', true) +
    '</div>' +
    '</div>'
  );
}

/**
 * Um botao de tab de ano. Provenience original: unified.html 1077-1082 (estilo
 * inline azul). Reforma 2026-06 (F4): estilo migrado para as classes
 * .u-year-tab/.active em observatory.css — o JS so alterna a classe
 * (setActiveTab) e o CSS pinta; syncYearTabs foi aposentado junto.
 */
function renderYearTab(year: string, active: boolean): string {
  const cssClass = active ? 'u-year-tab active' : 'u-year-tab';
  return `<button class="${cssClass}" data-year="${year}">${year}</button>`;
}

/** Grade-resumo de 3 cards: total, per capita e variacao YoY. */
function renderSummaryGrid(): string {
  return (
    '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">' +
    '<div style="background:var(--color-background-secondary);border-radius:var(--border-radius-md);padding:12px">' +
    '<div style="font-size:10px;color:var(--color-text-secondary);margin-bottom:4px">Arrecadação total</div>' +
    '<div id="u-imp-total" style="font-size:22px;font-weight:600;color:var(--color-text-primary);line-height:1">R$ 8,4 bi</div>' +
    '</div>' +
    '<div style="background:var(--color-background-secondary);border-radius:var(--border-radius-md);padding:12px">' +
    '<div style="font-size:10px;color:var(--color-text-secondary);margin-bottom:4px">Per capita</div>' +
    '<div id="u-imp-percapita" style="font-size:22px;font-weight:600;color:var(--color-text-primary);line-height:1">R$ 3.720</div>' +
    '</div>' +
    '<div style="background:#EAF3DE;border-radius:var(--border-radius-md);padding:12px">' +
    '<div style="font-size:10px;color:#3B6D11;margin-bottom:4px" id="u-imp-yoy-label">vs. 2024</div>' +
    '<div id="u-imp-yoy" style="font-size:22px;font-weight:600;color:#3B6D11;line-height:1"><i class="ti ti-trending-up" style="font-size:0.85em" aria-hidden="true"></i> 6,2%</div>' +
    '</div>' +
    '</div>'
  );
}

/**
 * Ranking ESTATICO inicial (unified.html 1103-1144). Substituido em runtime por
 * _updateRanking. Mantido verbatim para o estado pre-ativacao da secao.
 */
function renderStaticRanking(): string {
  return (
    '<div>' +
    '<div class="u-panel-section-label">Arrecadação per capita por zona</div>' +
    '<div id="u-ranking-rows">' +
    staticRankRow('1', 'Zona Centro-Sul', '+18% vs. média', '95%', '#1D9E75', 'R$ 6.420') +
    staticRankRow('2', 'Zona Sul', '+12% vs. média', '78%', '#1D9E75', 'R$ 5.240') +
    staticRankRow('3', 'Zona Leste', 'média', '55%', '#185FA5', 'R$ 3.720') +
    staticRankRow('4', 'Zona Centro-Oeste', '-8% vs. média', '42%', '#EF9F27', 'R$ 3.050') +
    staticRankRow('5', 'Zona Norte', '-22% vs. média', '28%', '#E24B4A', 'R$ 2.890') +
    staticRankRow('6', 'Zona Oeste', '-31% vs. média', '18%', '#E24B4A', 'R$ 2.380') +
    '</div>' +
    '</div>'
  );
}

/** Uma linha do ranking estatico (estrutura `<div>` de unified.html 1107-1112). */
function staticRankRow(
  position: string,
  zoneName: string,
  subLabel: string,
  barWidth: string,
  color: string,
  amountLabel: string,
): string {
  return (
    '<div class="u-ranking-row">' +
    `<div class="u-rank-pos">${position}</div>` +
    `<div class="u-rank-name">${zoneName}<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">${subLabel}</span></div>` +
    `<div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:${barWidth};background:${color}"></div></div>` +
    `<div class="u-rank-value" style="color:${color}">${amountLabel}</div>` +
    '</div>'
  );
}

/** Linha de CTAs (relatorio / comparar) com onclick inline preservado. */
function renderCtaRow(): string {
  return (
    '<div style="display:flex;gap:8px">' +
    '<button class="u-cta-btn" style="flex:1" onclick="sendPrompt(\'Gerar relatório fiscal completo de Manaus\')"><i class="ti ti-report-analytics"></i> Relatório completo</button>' +
    '<button class="u-cta-btn secondary" onclick="sendPrompt(\'Comparar arrecadação com outras cidades\')"><i class="ti ti-git-compare"></i> Comparar cidades</button>' +
    '</div>'
  );
}

/**
 * HTML do overlay injetado em #mapOverlays ao ativar a secao: dois headers do
 * split (Arrecadacao / Retorno), o divisor central e o contador flutuante.
 * Provenience: assets/unified-app.js 1914-1924 (_buildCounterOverlay).
 * @param entry entrada fiscal do tributo/ano corrente.
 * @param tribute tributo corrente ('icms' | 'iss') — define o rotulo.
 * @param year ano fiscal corrente — compoe o rotulo do contador.
 * @returns string HTML a inserir via insertAdjacentHTML('beforeend', ...).
 */
export function buildCounterOverlayHtml(
  entry: FiscalYearEntry,
  tribute: TaxKind,
  year: FiscalYear,
): string {
  const label = `${tribute === 'icms' ? 'ICMS' : 'ISS'} arrecadado · ${year}`;
  return (
    '<div class="u-split-header left" id="u-split-header-left"><i class="ti ti-arrow-up-right" style="color:#0F6E56"></i> <strong>Arrecadação</strong> per capita</div>' +
    '<div class="u-split-header right" id="u-split-header-right"><i class="ti ti-arrow-down-left" style="color:#185FA5"></i> <strong>Retorno</strong> / investimento</div>' +
    '<div class="u-split-divider" id="uo-split-divider"></div>' +
    '<div class="u-counter-float" id="uo-counter">' +
    `<div class="u-cf-label" id="uo-cf-label">${label}</div>` +
    `<div class="u-cf-value" id="uo-cf-value">${entry.total}</div>` +
    `<div class="u-cf-sub" id="uo-cf-sub">${yoyDeltaHtml(entry)}</div>` +
    '</div>'
  );
}
